/**
 * A share transfer — existing shares changing owner.
 *
 * Same frame as the shareholder and issuance records beside it. What makes it
 * its own screen is that it is the only record here that has to be *checked
 * against* the register rather than merely added to it: a transfer of more
 * shares than the seller holds is not a transfer, and the form is where that is
 * caught.
 *
 * ## Two things this form deliberately refuses to do
 *
 * **It does not create shares.** There is no "issue to buyer" path and no way to
 * reach one from here — an issuance is a different record, made by the company,
 * and conflating the two is how a cap table stops adding up. The lede on the tab
 * says so in the reader's terms.
 *
 * **It does not settle a transfer by saving it.** Settled is a checkbox, off by
 * default, because a transfer is usually recorded before the board consents and
 * the form is signed. Until it is ticked, the shares are still the seller's
 * everywhere in the app.
 *
 * ## Three buyers, and the third is not a person
 *
 * Selling to somebody new is the ordinary case for a secondary, so the buyer
 * side takes a name and an email and creates the shareholder on save. The record
 * it creates is deliberately thin — no address, no personnummer — and the form
 * says that those are needed before the next annual filing rather than blocking
 * the transfer on them.
 *
 * The third is the company itself. A buy-back has no buyer on the register:
 * Skida AS does not hold its own shares in its own aksjeeierbok, so it gets a
 * flag rather than a shareholder row, the shares are retired, and everybody
 * else's percentage rises. That last part is why the form says it out loud —
 * from the seller's side a buy-back and a sale look identical, and only one of
 * them changes what everyone else owns.
 */
import { useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import { fmtKrSum, parseKr } from '@/shared/util/money'
import {
  EMPTY_TRANSFER,
  sellerCapacity,
  type HolderKind,
  type ShareTransfer,
  type Shareholder,
} from '@/types/ownership'
import type { TransferInput } from './repository/ownershipRepository'
import '@/shared/ui/RecordForm.css'
import './Ownership.css'

interface Props {
  transfer: ShareTransfer
  isNew: boolean
  holders: Shareholder[]
  /** The whole ledger — the seller's capacity is a function of it. */
  transfers: ShareTransfer[]
  /** Shares outstanding, for "x % of the company". */
  outstanding: number
  onClose: () => void
  onSave: (draft: TransferInput, id?: string) => Promise<void>
  /** Creates the shareholder record for a buyer who is not on the register yet. */
  /**
   * Creates the record and returns its id. Takes the whole thin record rather
   * than a name and an email, so the kind travels with it — decision 99.
   */
  onCreateBuyer: (buyer: {
    name: string
    email: string
    kind: HolderKind
    orgNumber: string
    personnummer: string
  }) => Promise<string>
  /** Opens the board minutes for this buy-back. Absent on an ordinary transfer. */
  onBuybackMinutes: () => void
  onDelete: (id: string) => Promise<void>
}

const nb = (n: number) => n.toLocaleString('nb-NO')

type BuyerMode = 'existing' | 'new' | 'buyback'

export function TransferModal({
  transfer,
  isNew,
  holders,
  transfers,
  outstanding,
  onClose,
  onSave,
  onCreateBuyer,
  onBuybackMinutes,
  onDelete,
}: Props) {
  const [form, setForm] = useState<ShareTransfer>(transfer)
  const [buyerMode, setBuyerMode] = useState<BuyerMode>(
    transfer.buyback ? 'buyback' : 'existing',
  )
  /**
   * A buyer created here is typed, like one created anywhere else — decision 99.
   *
   * Until 25 Aug this branch collected a name and an email and nothing else, so
   * every buyer it made was a Person by `EMPTY_SHAREHOLDER`'s default and the
   * kind had to be corrected on the record afterwards. Three holders were
   * corrected to Company — Näslund × 2 and Eriksson Näslund — and the record
   * form then offered them the org.nr field, which is where their **full Swedish
   * personnummer** went. Org.nr is public information and is deliberately not
   * masked (decision 50), so the masking was not defeated by a bug in the
   * masking: the number was simply in the wrong field. Asking the question here
   * is what stops the correction being needed at all.
   */
  const [newBuyer, setNewBuyer] = useState<{
    name: string
    email: string
    kind: HolderKind
    orgNumber: string
    personnummer: string
  }>({ name: '', email: '', kind: 'person', orgNumber: '', personnummer: '' })
  const blankBuyer = { name: '', email: '', kind: 'person' as HolderKind, orgNumber: '', personnummer: '' }
  const [shares, setShares] = useState(transfer.shares ? String(transfer.shares) : '')
  const [price, setPrice] = useState(
    transfer.pricePerShare === null ? '' : String(transfer.pricePerShare),
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Editing the form clears a refusal that was about the form.
   *
   * The amber over-sale warning is derived and clears itself; the red banner is
   * a saved error and used to sit there until the next Save. Typing 9 999 shares
   * and then correcting it to 400 left a valid form wearing a rejection — which
   * reads as "this is still wrong" and is the kind of thing somebody works
   * around rather than reports.
   */
  const set = (patch: Partial<ShareTransfer>) => {
    setError(null)
    setForm((f) => ({ ...f, ...patch }))
  }

  const seller = holders.find((h) => h.id === form.fromId) ?? null
  const capacity = seller ? sellerCapacity(seller, transfers, isNew ? null : transfer.id) : 0
  const count = Math.round(Number(shares))
  const validCount = Number.isFinite(count) && count > 0
  // A price typed with a comma is a price. `Number` disagrees.
  const perShare = price.trim() === '' ? null : parseKr(price)
  const overSold = Boolean(seller) && validCount && count > capacity

  const consideration =
    validCount && perShare !== null && Number.isFinite(perShare) ? count * perShare : null
  const pctOfCompany = validCount && outstanding > 0 ? (count / outstanding) * 100 : null

  const save = async () => {
    if (!form.fromId) {
      setError('A transfer needs a seller.')
      return
    }
    if (!validCount) {
      setError('A transfer needs a share count above zero.')
      return
    }
    if (overSold) {
      setError(`That is more than the seller holds (${nb(capacity)}).`)
      return
    }
    if (!form.date) {
      setError('A transfer needs a date — it is the entry in the register.')
      return
    }
    if (buyerMode === 'existing' && !form.toId) {
      setError('A transfer needs a buyer. Pick one, or add a new shareholder.')
      return
    }
    if (buyerMode === 'existing' && form.toId === form.fromId) {
      setError('The seller and the buyer are the same person.')
      return
    }
    if (buyerMode === 'new' && !newBuyer.name.trim()) {
      setError('A new buyer needs a name — it goes on the register.')
      return
    }
    if (buyerMode === 'buyback' && !form.note.trim()) {
      // The one field this form insists on, and only here. A buy-back lowers
      // the share capital everybody else's percentage is measured against, and
      // an entry in the register that does not say which resolution allowed it
      // is not an entry anybody can act on later.
      setError('A buy-back needs a note — which resolution allowed it.')
      return
    }
    if (price.trim() !== '' && perShare === null) {
      setError('That price per share is not a number.')
      return
    }

    setSaving(true)
    setError(null)
    try {
      // The buyer is created first: a transfer pointing at a shareholder that
      // does not exist yet would read as a transfer to nobody for as long as the
      // second write took, and would stay that way if it failed.
      const toId =
        buyerMode === 'new'
          ? await onCreateBuyer({
              name: newBuyer.name.trim(),
              email: newBuyer.email.trim().toLowerCase(),
              kind: newBuyer.kind,
              // Only the identifier the kind actually has. A company carries no
              // personnummer and a person carries no org.nr, and writing the
              // other one is how the two get confused in the first place.
              orgNumber: newBuyer.kind === 'company' ? newBuyer.orgNumber.replace(/\D/g, '') : '',
              personnummer: newBuyer.kind === 'person' ? newBuyer.personnummer.trim() : '',
            })
          : buyerMode === 'buyback'
            ? ''
            : form.toId
      await onSave(
        {
          date: form.date,
          fromId: form.fromId,
          toId,
          buyback: buyerMode === 'buyback',
          shares: count,
          pricePerShare: perShare,
          settled: form.settled,
          note: form.note.trim(),
        },
        isNew ? undefined : transfer.id,
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={isNew ? 'Record share transfer' : 'Transfer'}
      onClose={onClose}
      width={620}
      footer={
        <>
          {!isNew && (
            <button
              className="btn btn--ghost recform__delete"
              disabled={saving}
              onClick={() => void onDelete(transfer.id)}
            >
              Delete transfer
            </button>
          )}
          <button className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn--primary" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save transfer'}
          </button>
        </>
      }
    >
      <div className="recform">
        <p className="recform__note own__lede">
          {buyerMode === 'buyback'
            ? 'Existing shares returning to the company. Nothing new is issued; the shares are retired and the total outstanding falls.'
            : 'Existing shares changing owner. Nothing new is issued and the total outstanding stays the same.'}
        </p>

        <section className="recform__group">
          <div className="recform__grid">
            <label className="field recform__wide">
              <span>Seller</span>
              <select
                className="input"
                value={form.fromId}
                onChange={(e) => set({ fromId: e.target.value })}
              >
                <option value="">Select shareholder…</option>
                {holders.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="recform__note">
            {seller
              ? `${seller.name} holds ${nb(capacity)} shares.`
              : 'Pick the seller first — the share count is checked against what they hold.'}
          </p>
        </section>

        <section className="recform__group">
          <h4 className="recform__legend">Buyer</h4>
          <div className="own__tabs">
            <button
              className="chip"
              aria-pressed={buyerMode === 'existing'}
              onClick={() => {
                setBuyerMode('existing')
                setNewBuyer(blankBuyer)
              }}
            >
              Existing shareholder
            </button>
            <button
              className="chip"
              aria-pressed={buyerMode === 'new'}
              onClick={() => {
                setBuyerMode('new')
                set({ toId: '' })
              }}
            >
              New shareholder
            </button>
            <button
              className="chip"
              aria-pressed={buyerMode === 'buyback'}
              onClick={() => {
                setBuyerMode('buyback')
                setNewBuyer(blankBuyer)
                set({ toId: '' })
              }}
            >
              Company buy-back
            </button>
          </div>
          {buyerMode === 'buyback' ? (
            <p className="recform__warn">
              Skida AS buys the shares back and they leave the register. Shares outstanding falls
              by {validCount ? nb(count) : 'the amount below'}, and every remaining holder&rsquo;s
              percentage rises without anybody buying anything.
            </p>
          ) : buyerMode === 'existing' ? (
            <div className="recform__grid">
              <label className="field recform__wide">
                <span>Which one</span>
                <select
                  className="input"
                  value={form.toId}
                  onChange={(e) => set({ toId: e.target.value })}
                >
                  <option value="">Select shareholder…</option>
                  {holders
                    .filter((h) => h.id !== form.fromId)
                    .map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.name}
                      </option>
                    ))}
                </select>
              </label>
            </div>
          ) : (
            <>
              {/* Decision 99: the same Person / Company choice as + New
                  shareholder, and it comes first because it decides which
                  identifier the form asks for below. */}
              <div className="own__tabs own__tabs--kind">
                <button
                  className="chip"
                  aria-pressed={newBuyer.kind === 'person'}
                  onClick={() => setNewBuyer({ ...newBuyer, kind: 'person', orgNumber: '' })}
                >
                  Person
                </button>
                <button
                  className="chip"
                  aria-pressed={newBuyer.kind === 'company'}
                  onClick={() => setNewBuyer({ ...newBuyer, kind: 'company', personnummer: '' })}
                >
                  Company
                </button>
              </div>
              <div className="recform__grid">
                <label className="field">
                  <span>{newBuyer.kind === 'company' ? 'Legal name' : 'Full name'}</span>
                  <input
                    className="input"
                    value={newBuyer.name}
                    onChange={(e) => setNewBuyer({ ...newBuyer, name: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span>Email</span>
                  <input
                    className="input"
                    value={newBuyer.email}
                    onChange={(e) => setNewBuyer({ ...newBuyer, email: e.target.value })}
                  />
                </label>
                {/* One identifier, and it follows the kind. Never both fields on
                    screen at once: two boxes that look alike is how a
                    personnummer ends up in the org.nr column. */}
                {newBuyer.kind === 'company' ? (
                  <label className="field recform__wide">
                    <span>Org.nr</span>
                    <input
                      className="input"
                      inputMode="numeric"
                      placeholder="9 siffer"
                      value={newBuyer.orgNumber}
                      onChange={(e) => setNewBuyer({ ...newBuyer, orgNumber: e.target.value })}
                    />
                    <span className="field__note">
                      Public information from Enhetsregisteret, and not masked — hiding a public
                      number would teach the reader that the masking elsewhere is decoration.
                    </span>
                  </label>
                ) : (
                  <label className="field recform__wide">
                    <span>Personnummer</span>
                    <input
                      className="input own__pnr"
                      inputMode="numeric"
                      placeholder="11 siffer, eller som landet skriver det"
                      value={newBuyer.personnummer}
                      onChange={(e) => setNewBuyer({ ...newBuyer, personnummer: e.target.value })}
                    />
                    <span className="field__note">
                      Optional here. It is in no list view at any width, admin-only in the rules,
                      and can be added on the record before the next filing.
                    </span>
                  </label>
                )}
              </div>
              <p className="recform__note">
                A shareholder record is created on save — add the address there before the next
                annual filing.
              </p>
            </>
          )}
        </section>

        <section className="recform__group">
          <div className="recform__grid">
            <label className="field">
              <span>Shares</span>
              <input
                className="input"
                inputMode="numeric"
                value={shares}
                onChange={(e) => {
                  // Not part of `form`, so `set` does not run for it — and this is
                  // the field the over-sale refusal is about.
                  setError(null)
                  setShares(e.target.value)
                }}
              />
            </label>
            <label className="field">
              <span>Price per share (kr)</span>
              <input
                className="input"
                inputMode="decimal"
                value={price}
                onChange={(e) => {
                  setError(null)
                  setPrice(e.target.value)
                }}
              />
            </label>
            <label className="field">
              <span>Transfer date</span>
              <input
                className="input"
                type="date"
                value={form.date}
                onChange={(e) => set({ date: e.target.value })}
              />
            </label>
            {/* Two pills rather than a checkbox: this is a state a transfer is
                in, not an option to switch on, and the table row toggles the
                same two words in place. */}
            <label className="field own__settled">
              <span>Status</span>
              <div className="own__pills">
                <button
                  className="chip"
                  aria-pressed={!form.settled}
                  onClick={() => set({ settled: false })}
                >
                  Pending
                </button>
                <button
                  className="chip"
                  aria-pressed={form.settled}
                  onClick={() => set({ settled: true })}
                >
                  Settled
                </button>
              </div>
            </label>
            <label className="field recform__wide">
              <span>Note</span>
              <input
                className="input"
                value={form.note}
                placeholder="Board consent, share transfer form, pre-emption waiver…"
                onChange={(e) => set({ note: e.target.value })}
              />
            </label>
          </div>
          <p className="recform__note">
            {form.settled
              ? 'Settled — the shares have moved and count from the transfer date.'
              : 'Pending — the shares stay with the seller until this is settled.'}
          </p>
          <p className="recform__note">
            {consideration === null
              ? 'Consideration —'
              : `Consideration ${fmtKrSum(consideration)}`}
            {pctOfCompany === null
              ? ''
              : ` · ${pctOfCompany.toFixed(2).replace('.', ',')} % of the company`}
          </p>
        </section>

        {/* Amber, not the red error line: over-selling is the consequence of a
            number just typed, which is exactly what DECISIONS.md reserves amber
            prose for. It shows while typing; the red line only appears on Save. */}
        {overSold && (
          <p className="recform__warn">
            That is more than the seller holds ({nb(capacity)}). A transfer can only move shares
            that exist — issue new ones under Issuances instead.
          </p>
        )}

        {/* A buy-back is a board decision and produces its own minute — a
            different document from a capital increase, per decision 51. It
            reads the saved record, so it appears once there is one. */}
        {!isNew && transfer.buyback && (
          <section className="recform__group">
            <h4 className="recform__legend">Documents</h4>
            <div className="recform__docs">
              <button className="btn btn--outline" onClick={onBuybackMinutes} disabled={saving}>
                Board minutes
              </button>
            </div>
            <p className="recform__note">
              Generated in Norwegian from the register: authority under § 9-4, the free-equity limit
              under § 9-3 as a gap to confirm, and the consequence that own shares carry no vote.
              Retiring them is a separate resolution and the minute says so.
            </p>
          </section>
        )}

        {error && <p className="recform__error">{error}</p>}
      </div>
    </Modal>
  )
}

export const emptyTransfer = (): ShareTransfer => ({ id: '', ...EMPTY_TRANSFER })
